import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from 'react-bootstrap/Modal';
import Dropdown from 'react-bootstrap/Dropdown';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import JalaliDatePicker from '../../components/common/JalaliDatePicker';
import { apiClient, BASE_URL } from '../../components/api/ApiClient';
import { formatInvoiceSubject } from '../../utils/invoiceUtils';
import { gregorianToJalali, jalaliToGregorian, parseJalaliDate } from '../../utils/jalaliDate';

function Invoices() {
    const navigate = useNavigate();
    const [invoices, setInvoices] = useState({ data: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedInvoices, setSelectedInvoices] = useState([]);
    const [showMoadianModal, setShowMoadianModal] = useState(false);
    const [showFilterDropdown, setShowFilterDropdown] = useState(false);
    const [moadianFiles, setMoadianFiles] = useState({
        privateKeyFile: null,
        certificateFile: null
    });
    const [selectedMoadianInvoices, setSelectedMoadianInvoices] = useState([]);
    const [toast, setToast] = useState({ show: false, message: '', type: 'info' });
    const [errorModal, setErrorModal] = useState({ show: false, invoice: null, errors: [] });

    const [filters, setFilters] = useState({
        dateFrom: '',
        dateTo: '',
        invoiceNumFrom: '',
        invoiceNumTo: '',
        branch: '',
        moadianStatus: ''
    });
    const [branches, setBranches] = useState([]);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);

    const privateKeyFileInputRef = useRef(null);
    const certificateFileInputRef = useRef(null);

    const moadianStatuses = [
        { value: 0, label: 'ارسال نشده' },
        { value: 0.25, label: 'ارسال نشده (دوباره ارسال کنید)' },
        { value: 0.5, label: 'در حال پردازش' },
        { value: 1, label: 'ارسال شده (ناموفق)' },
        { value: 1.5, label: 'ارسال شده (در حال پردازش)' },
        { value: 2, label: 'ارسال شده (موفق)' }
    ];

    const getStatusLabel = (status) => {
        const found = moadianStatuses.find(s => s.value == status);
        return found ? found.label : 'ارسال نشده';
    };

    const getStatusBadgeClass = (status) => {
        const s = Number(status);
        switch (s) {
            case 0: return 'bg-danger';
            case 0.25: return 'bg-warning';
            case 0.5: return 'bg-info';
            case 1: return 'bg-danger';
            case 1.5: return 'bg-primary';
            case 2: return 'bg-success';
            default: return 'bg-secondary';
        }
    };

    const toShamsiDate = (dateStr) => {
        if (!dateStr) return '-';
        try {
            // Check if it already looks like Jalali (Simple check: year start with 13 or 14)
            if (String(dateStr).startsWith('13') || String(dateStr).startsWith('14')) {
                return dateStr;
            }

            const dateObj = new Date(dateStr);
            if (isNaN(dateObj)) return dateStr;
            const j = gregorianToJalali(dateObj.getFullYear(), dateObj.getMonth() + 1, dateObj.getDate());
            const z = (n) => String(n).padStart(2, '0');
            return `${j.year}/${z(j.month)}/${z(j.day)} ${z(dateObj.getHours())}:${z(dateObj.getMinutes())}`;
        } catch (e) {
            return dateStr;
        }
    };

    const convertJalaliToGregorianStr = (jalaliStr) => {
        if (!jalaliStr) return '';
        try {
            const parsed = parseJalaliDate(jalaliStr);
            if (!parsed) return jalaliStr;
            const g = jalaliToGregorian(parsed.year, parsed.month, parsed.day);
            const z = (n) => String(n).padStart(2, '0');
            
            const hour = parsed.hour !== undefined ? parsed.hour : 0;
            const minute = parsed.minute !== undefined ? parsed.minute : 0;
            return `${z(hour)}:${z(minute)} ${g.year}/${z(g.month)}/${z(g.day)}`;
        } catch (e) {
            console.error("Error converting date:", e);
            return jalaliStr;
        }
    };

    useEffect(() => {
        if (toast.show) {
            const timer = setTimeout(() => setToast({ ...toast, show: false }), 5000);
            return () => clearTimeout(timer);
        }
    }, [toast.show]);

    const showToast = (message, type = 'info') => {
        setToast({ show: true, message, type });
    };

    useEffect(() => {
        fetchInvoices();
        fetchBranches();
        
        const intervalId = setInterval(() => {
            fetchInvoices();
        }, 30000);
        
        return () => clearInterval(intervalId);
    }, []);

    const fetchBranches = async () => {
        try {
            const response = await apiClient('/invoice/api/new-invoice/');
            if (response && response.data && Array.isArray(response.data.branches)) {
                setBranches(response.data.branches);
            } else if (response && Array.isArray(response.branches)) {
                setBranches(response.branches);
            }
        } catch (error) {
            console.error("Error fetching branches:", error);
        }
    };

    const fetchInvoices = async (filterParams = {}) => {
        setLoading(true);
        try {
            const queryParams = {};
            if (filterParams.date_from) queryParams.date_from = convertJalaliToGregorianStr(filterParams.date_from);
            if (filterParams.date_to) queryParams.date_to = convertJalaliToGregorianStr(filterParams.date_to);
            if (filterParams.invoice_num_from) queryParams.invoice_num_from = filterParams.invoice_num_from;
            if (filterParams.invoice_num_to) queryParams.invoice_num_to = filterParams.invoice_num_to;
            if (filterParams.branch) queryParams.branch = filterParams.branch;
            if (filterParams.moadian_status) queryParams.moadian_status = filterParams.moadian_status;
            if (filterParams.page) queryParams.page = filterParams.page;
            else{
                 filterParams.page = 1;
                 queryParams.page = filterParams.page;
                }
            const data = await apiClient('/invoice/api/', { 
                method: 'GET',
                params: queryParams
            });
            if (data && data.data && Array.isArray(data.data)) {
                const invoicesWithErrors = data.data.map(inv => ({
                    ...inv,
                    moadianErrors: inv.moadian_statuses?.errors || inv.moadianErrors || null
                }));
                invoicesWithErrors.sort((a, b) => b.id - a.id);
                setInvoices({ ...data, data: invoicesWithErrors });
                setCurrentPage(filterParams.page || 1);
            } else {
                setInvoices(data || { data: [] });
            }
            setError(null);
        } catch (err) {
            console.error("خطا در دریافت صورتحساب‌ها:", err);
            setError(`خطا در دریافت اطلاعات: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedInvoices(invoices.data.map(inv => inv.id));
        } else {
            setSelectedInvoices([]);
        }
    };

    const handleSelectInvoice = (id) => {
        setSelectedInvoices(prev => {
            if (prev.includes(id)) {
                return prev.filter(invId => invId !== id);
            } else {
                return [...prev, id];
            }
        });
    };

    const handleExportToExcel = async () => {
        if (selectedInvoices.length === 0) {
            showToast('لطفاً حداقل یک صورتحساب را انتخاب کنید', 'warning');
            return;
        }

        try {
            const response = await apiClient('/invoice/api/invoice-to-excel/', {
                method: 'POST',
                data: { data: selectedInvoices },
                responseType: 'blob'
            });

            const blob = new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `invoices_${new Date().getTime()}.xlsx`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            showToast('فایل اکسل با موفقیت دانلود شد', 'success');
        } catch (err) {
            showToast(`خطا در تبدیل به اکسل: ${err.message}`, 'error');
        }
    };

    const handleSendToMoadian = async () => {
        if (selectedInvoices.length === 0) {
            showToast('لطفاً حداقل یک صورتحساب را انتخاب کنید', 'warning');
            return;
        }

        const selectedData = invoices.data.filter(inv => selectedInvoices.includes(inv.id));
        
        try {
            const companyResponse = await apiClient('/companies/api/');
            const branchesData = companyResponse.branches || [];
            
            const firstInvoice = selectedData[0];
            
            const branch = firstInvoice?.branch;
            const branchId = branchesData.find(b => b.id === branch.id).id || branch.id;
            const acceptContract = branchesData.find(b => b.id === branch.id).acceptContract
            
            if (acceptContract) {
                const selectedInvoiceData = selectedData.map(inv => ({
                    id: inv.id,
                    invoiceFinalDate: inv.finalDate,
                    invoiceNum: inv.invoiceNum,
                    invoicePrice: inv.sumTotalPrice
                }));

                const payload = {
                    invoiceId: selectedInvoiceData,
                    task: "send",
                    branchId: branchId
                };

                await apiClient('/invoice/api/moadian-system/', {
                    method: 'POST',
                    data: payload
                });

                showToast('صورتحساب‌ها با موفقیت ارسال شدند', 'success');
                fetchInvoices();
            } else {
                setSelectedMoadianInvoices(selectedData);
                setShowMoadianModal(true);
            }
        } catch (err) {
            showToast('خطا در ارسال به مودیان: ' + (err.message || 'خطای سرور'), 'error');
            setShowMoadianModal(false);
        }
    };

    const handleFileChange = (e, type) => {
        const file = e.target.files[0];
        if (file) {
            setMoadianFiles(prev => ({
                ...prev,
                [type]: file
            }));
        }
    };

    const submitToMoadian = async (isTest = false) => {
        const selectedInvoiceData = selectedMoadianInvoices.map(inv => ({
            id: inv.id,
            invoiceFinalDate: inv.finalDate,
            invoiceNum: inv.invoiceNum,
            invoicePrice: inv.sumTotalPrice
        }));

        const acceptContract = selectedMoadianInvoices[0]?.branch?.acceptContract;

        if (!acceptContract) {
            if (!moadianFiles.privateKeyFile || !moadianFiles.certificateFile) {
                showToast('لطفاً هر دو فایل را انتخاب کنید', 'warning');
                return;
            }
        }

        const readFileAsText = (file) => {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = (e) => resolve(e.target.result);
                reader.onerror = reject;
                reader.readAsText(file);
            });
        };

        try {
            let privateKeyContent = "";
            let certificateContent = "";

            if (!acceptContract) {
                privateKeyContent = await readFileAsText(moadianFiles.privateKeyFile);
                certificateContent = await readFileAsText(moadianFiles.certificateFile);
            }

            const payload = {
                invoiceId: selectedInvoiceData,
                task: isTest ? "test" : "send",
                privateKey: privateKeyContent,
                certificate: certificateContent
            };

            await apiClient('/invoice/api/moadian-system/', {
                method: 'POST',
                data: payload
            });

            showToast(`صورتحساب‌ها با موفقیت ${isTest ? 'تست' : 'ارسال'} شدند`, 'success');
            setShowMoadianModal(false);
            setMoadianFiles({ privateKeyFile: null, certificateFile: null });

        } catch (err) {
            setShowMoadianModal(false);
            setMoadianFiles({ privateKeyFile: null, certificateFile: null });
            showToast('خطا در ارسال به مودیان: ' + (err.message || 'خطای نامشخص'), 'error');
        }
    };


    const handleAssignUniqueId = async (invoice) => {
        const payload = {
            target: {
                id: invoice.id,
                branch: invoice.branch || null,
                invoiceNum: invoice.invoiceNum,
                invoiceFinalDate: invoice.finalDate,
                invoicePrice: invoice.sumTotalPrice,
                invoiceAddedValue: invoice.sumTaxPrice || 0,
                branchCode: invoice.branchCode || null,
                buyerTaxId: invoice.buyerTaxId || null,
                uniqueIdentifier: invoice.uniqueIdentifier || null,
                madianStatus: invoice.moadianStatus || null,
                acceptContract: invoice.acceptContract || null,
                invoiceSubject: invoice.invoiceSubject || "First",
                refInvoice: invoice.refInvoice || null,
                arsinStatus: invoice.arsinStatus || "0"
            }
        };

        try {
            await apiClient('/invoice/api/unique-identifier/', {
                method: 'POST',
                data: payload
            });
            showToast('شناسه یکتا با موفقیت اختصاص یافت', 'success');
            fetchInvoices();
        } catch (err) {
            showToast('خطا در اختصاص شناسه یکتا', 'error');
        }
    };

    const handleViewInvoice = (id) => {
        navigate(`/invoice/view/${id}`, { state: { mode: 'view' } });
    };

    const handleEditInvoice = (id) => {
        navigate(`/invoice/edit/${id}`, { state: { mode: 'edit' } });
    };

    const handlePrintInvoice = async (id) => {
        try {
            const blob = await apiClient('/invoice/api/download-invoice/', {
                method: 'POST',
                data: { id },
                responseType: 'blob'
            });
            
            const file = new Blob([blob], { type: 'application/pdf' });
            const fileURL = URL.createObjectURL(file);
            
            let opened = false;
            try {
                const pdfWindow = window.open(fileURL, '_blank');
                if (pdfWindow) {
                    opened = true;
                }
            } catch (windowOpenError) {
                console.error("Failed to open PDF window:", windowOpenError);
            }

            if (!opened) {
                // Fallback to direct download if browser popup-blocker is active
                const link = document.createElement('a');
                link.href = fileURL;
                link.download = `invoice-${id}.pdf`;
                link.click();
            }
        } catch (err) {
            console.error("Print error:", err);
            const statusCode = err.statusCode;
            let userMessage = err.message || 'خطا در دریافت صورتحساب';
            if (statusCode === 504) {
                userMessage = 'خطای ۵۰۴: سرور در زمان مجاز پاسخ نداد. لطفاً دوباره تلاش کنید.';
            } else if (statusCode === 502) {
                userMessage = 'خطای ۵۰۲: سرور موقتاً در دسترس نیست. لطفاً دوباره تلاش کنید.';
            } else if (statusCode >= 500) {
                userMessage = `خطای سرور (${statusCode}): ${err.message || 'لطفاً دوباره تلاش کنید.'}`;
            } else if (statusCode >= 400) {
                userMessage = `خطا (${statusCode}): ${err.message || 'درخواست نامعتبر'}`;
            }
            showToast(userMessage, 'error');
        }
    };

    const handleCorrectionInvoice = (invoice) => {
        navigate(`/invoice/correction/${invoice.id}`, {
            state: {invoice: invoice}
        });
    };

    const handleCancellationInvoice = (invoice) => {
        navigate(`/invoice/cancellation/${invoice.id}`, {
            state: {invoice: invoice}
        });
    };

    const handleReturnedInvoice = (invoice) => {
        navigate(`/invoice/returned/${invoice.id}`, {
            state: {invoice: invoice}
        });
    };

    const handleSendSingleToMoadian = async (invoice) => {
        try {
            const companyResponse = await apiClient('/companies/api/');
            const branchesData = companyResponse.branches || [];
            
            const branch = invoice?.branch;
            const branchId = branchesData.find(b => b.id === branch.id).id || branch.id;
            const acceptContract = branchesData.find(b => b.id === branch.id).acceptContract
            if (acceptContract) {
                const payload = {
                    invoiceId: [{
                        id: invoice.id,
                        invoiceFinalDate: invoice.finalDate,
                        invoiceNum: invoice.invoiceNum,
                        invoicePrice: invoice.sumTotalPrice
                    }],
                    task: "send",
                    branchId: branchId
                };

                await apiClient('/invoice/api/moadian-system/', {
                    method: 'POST',
                    data: payload
                });

                showToast('صورتحساب با موفقیت ارسال شد', 'success');
                fetchInvoices();
            } else {
                setSelectedMoadianInvoices([invoice]);
                setShowMoadianModal(true);
            }
        } catch (err) {
            showToast('خطا در ارسال به مودیان: ' + (err.message || 'خطای سرور'), 'error');
        }
    };

    const fixMoadianText = (text) => {
        if (!text) return '';
        text = String(text).replace(/\s*\n\s*/g, ' ');
        text = text.replace(/\.\s*\(/g, '.(');
        text = text.replace(/\)\s+(در مقدار)/g, ')\n$1');
        text = text.replace(/\.\s+(در مقدار)/g, '.\n$1');
        return text;
    };

    const handleViewErrors = async (invoice) => {
        try {
            if (invoice.moadianErrors) {
                setErrorModal({ 
                    show: true, 
                    invoice, 
                    errors: typeof invoice.moadianErrors === 'string' 
                        ? fixMoadianText(invoice.moadianErrors).split('\n').filter(e => e.trim())
                        : Array.isArray(invoice.moadianErrors) 
                            ? invoice.moadianErrors 
                            : [String(invoice.moadianErrors)]
                });
                return;
            }

            const response = await apiClient('/invoice/api/moadian-system/', {
                method: 'POST',
                data: { invoiceId: [{ id: invoice.id }], task: 'check' }
            });
            
            if (response && response.errors) {
                const errors = typeof response.errors === 'string' 
                    ? fixMoadianText(response.errors).split('\n').filter(e => e.trim())
                    : Array.isArray(response.errors) 
                        ? response.errors 
                        : [String(response.errors)];
                
                if (errors.length > 0) {
                    setErrorModal({ show: true, invoice, errors });
                } else {
                    showToast('خطایی یافت نشد', 'info');
                }
            } else {
                showToast('خطایی یافت نشد', 'info');
            }
        } catch (err) {
            showToast('خطا در دریافت خطاها', 'error');
        }
    };

    const handleSendToArsin = async (invoice) => {
        try {
            const response = await apiClient('/invoice/api/arsin-system/', {
                data: {data: {id: invoice.id}},
                method: "POST",
            })
            fetchInvoices();
            showToast('صورتحساب با موفقیت به شبکه ارسین ارسال شد', 'success')
        } catch (e) {
            console.log(e)
            if (e.statusCode === 404) {
                showToast('خریدار در شبکه ارسین عضو نیست!', 'error')
            } else if (e.statusCode === 400) {
                showToast('مشکلی پیش امده است', 'error')
            } else if (e.statusCode === 409) {
                showToast('این صورتحساب قبلا به شبکه ارسین ارسال شده است', 'error')
            }
        }
    };

    const handlePrintSaleDoc = async (savedDocId) => {
        if (savedDocId == null) return;
        try {
            const token = localStorage.getItem('access_token');
            const printUrl = `${BASE_URL}/purchase/api/sale-accounting-document/print/?doc_id=${savedDocId}`;

            const response = await fetch(printUrl, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (!response.ok) {
                const errText = await response.text().catch(() => '');
                throw new Error(`خطای سرور ${response.status}${errText ? ': ' + errText : ''}`);
            }

            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            const win = window.open(blobUrl, '_blank');
            if (!win) {
                showToast('پاپ‌آپ مرورگر مسدود است. لطفاً آن را در تنظیمات فعال کنید.', 'error');
            }
        } catch (err) {
            showToast(err.message || 'خطا در چاپ سند', 'error');
        }
    };

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [invoiceToDelete, setInvoiceToDelete] = useState(null);

    const handleDeleteInvoice = (invoice) => {
        setInvoiceToDelete(invoice);
        setShowDeleteModal(true);
    };

    const confirmDeleteInvoice = async () => {
        if (!invoiceToDelete) return;
        try {
            await apiClient('/invoice/api/', {
                method: 'DELETE',
                data: { id: invoiceToDelete.id }
            });
            showToast('صورتحساب با موفقیت حذف شد', 'success');
            fetchInvoices();
        } catch (err) {
            showToast('خطا در حذف صورتحساب', 'error');
        } finally {
            setShowDeleteModal(false);
            setInvoiceToDelete(null);
        }
    };


    const handlePageChange = (page) => {
        const filterParams = {};
        if (filters.dateFrom) filterParams.date_from = filters.dateFrom;
        if (filters.dateTo) filterParams.date_to = filters.dateTo;
        if (filters.invoiceNumFrom) filterParams.invoice_num_from = filters.invoiceNumFrom;
        if (filters.invoiceNumTo) filterParams.invoice_num_to = filters.invoiceNumTo;
        if (filters.branch) filterParams.branch = filters.branch;
        if (filters.moadianStatus) filterParams.moadian_status = filters.moadianStatus;
        filterParams.page = page;
        fetchInvoices(filterParams);
    };

    const getTotalPages = () => {
        if (invoices.count) return Math.ceil(invoices.count / pageSize);
        if (invoices.total_pages) return invoices.total_pages;
        return 1;
    };

    const renderPagination = () => {
        const totalPages = getTotalPages();
        if (totalPages <= 1) return null;
        const pages = [];
        const maxVisible = 5;
        let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let end = Math.min(totalPages, start + maxVisible - 1);
        if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);

        return (
            <div className="d-flex justify-content-center align-items-center mt-3 px-1" dir="rtl">
                <div className="text-muted small">
                    {invoices.count ? `مجموع: ${invoices.count.toLocaleString('fa')} صورتحساب` : ''}
                </div>
                <nav>
                    <ul className="pagination pagination-sm mb-0">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => handlePageChange(1)}>«</button>
                        </li>
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => handlePageChange(currentPage - 1)}>‹</button>
                        </li>
                        {start > 1 && <li className="page-item disabled"><span className="page-link">...</span></li>}
                        {Array.from({ length: end - start + 1 }, (_, i) => start + i).map(p => (
                            <li key={p} className={`page-item ${p === currentPage ? 'active' : ''}`}>
                                <button className="page-link" onClick={() => handlePageChange(p)}>{p.toLocaleString('fa')}</button>
                            </li>
                        ))}
                        {end < totalPages && <li className="page-item disabled"><span className="page-link">...</span></li>}
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => handlePageChange(currentPage + 1)}>›</button>
                        </li>
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => handlePageChange(totalPages)}>»</button>
                        </li>
                    </ul>
                </nav>
            </div>
        );
    };

    const handleApplyFilters = () => {
        const filterParams = {};

        if (filters.dateFrom) {
            filterParams.date_from = filters.dateFrom;
        }

        if (filters.dateTo) {
            filterParams.date_to = filters.dateTo;
        }

        if (filters.invoiceNumFrom) {
            filterParams.invoice_num_from = filters.invoiceNumFrom;
        }

        if (filters.invoiceNumTo) {
            filterParams.invoice_num_to = filters.invoiceNumTo;
        }

        if (filters.branch) {
            filterParams.branch = filters.branch;
        }

        if (filters.moadianStatus) {
            filterParams.moadian_status = filters.moadianStatus;
        }

        filterParams.page = 1;

        fetchInvoices(filterParams);
        setShowFilterDropdown(false);
    };

    const clearFilters = () => {
        setFilters({
            dateFrom: '',
            dateTo: '',
            invoiceNumFrom: '',
            invoiceNumTo: '',
            branch: '',
            moadianStatus: ''
        });
        fetchInvoices();
        setShowFilterDropdown(false);
    };

    const hasActiveFilters = () => {
        return !!(filters.dateFrom || filters.dateTo || filters.invoiceNumFrom || filters.invoiceNumTo || filters.branch || filters.moadianStatus);
    };

    return (
        <div className="row">
            {toast.show && (
                <div aria-live="polite" aria-atomic="true" className="position-fixed w-100 p-3" style={{ zIndex: 1060, top: 0, right: 0 }}>
                    <div className="toast-container position-absolute p-3" style={{ top: 0, left: 0 }}>
                        <div className="toast show" role="alert" aria-live="assertive" aria-atomic="true">
                            <div className="toast-header" style={{ direction: 'rtl' }}>
                                <img src="/assets/images/redesigned.png" className="rounded ms-2" alt="..." width="20" />
                                <strong className="me-auto ms-2">پیام سیستم</strong>
                                <button type="button" className="btn-close" onClick={() => setToast({ ...toast, show: false })}></button>
                            </div>
                            <div className="toast-body">
                                {toast.message}
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            <div className="col-lg-12">
                <div className="card stretch stretch-full">
                    <div className="card-header">
                        <h5 className="card-title">صورتحساب‌ها</h5>
                        <div className="d-flex gap-2">
                            <OverlayTrigger placement="top" overlay={<Tooltip>بروزرسانی لیست صورتحساب</Tooltip>}>
                                <button className="btn btn-warning" onClick={() => fetchInvoices()}>
                                    <i className="feather-refresh-cw fs-4"></i>
                                </button>
                            </OverlayTrigger>
                            <OverlayTrigger placement="top" overlay={<Tooltip>تبدیل صورتحساب به اکسل</Tooltip>}>
                                <button className="btn btn-success" onClick={handleExportToExcel}>
                                    <i className="feather-file-text fs-4"></i>
                                </button>
                            </OverlayTrigger>
                            <OverlayTrigger placement="top" overlay={<Tooltip>ارسال صورتحساب به سامانه مودیان</Tooltip>}>
                                <button className="btn btn-primary" onClick={handleSendToMoadian}>
                                    <i className="feather-send fs-4"></i>
                                </button>
                            </OverlayTrigger>
                        </div>
                    </div>

                    <div className="card-body">
                        <div className="row mb-4">
                            <div className="col-md-12">
                                <div className="position-relative d-flex gap-2">
                                    <button
                                        className="btn btn-outline-primary btn-sm mb-3"
                                        onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                                    >
                                        <i className="feather-filter me-1"></i>
                                        فیلتر
                                    </button>

                                    {hasActiveFilters() && (
                                        <button
                                            className="btn btn-outline-danger btn-sm mb-3"
                                            onClick={clearFilters}
                                        >
                                            <i className="feather-x me-1"></i>
                                            حذف فیلترها
                                        </button>
                                    )}

                                    {showFilterDropdown && (
                                        <div className="border rounded p-4 shadow-sm bg-white position-absolute w-100" style={{ zIndex: 10, top: '40px' }}>
                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                <h6 className="mb-0 fw-bold">فیلترهای جستجو</h6>
                                                <button
                                                    className="btn-close"
                                                    onClick={() => setShowFilterDropdown(false)}
                                                ></button>
                                            </div>

                                            <div className="row g-3">
                                                 <div className="col-md-3">
                                                    <label className="form-label">شعبه</label>
                                                    <select
                                                        className="form-select"
                                                        value={filters.branch}
                                                        onChange={(e) => setFilters({ ...filters, branch: e.target.value })}
                                                    >
                                                        <option value="">همه</option>
                                                        {branches.map(b => (
                                                            <option key={b.id} value={b.id}>{b.name || b.code}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="col-md-3">
                                                    <label className="form-label">وضعیت سامانه مودیان</label>
                                                    <select
                                                        className="form-select"
                                                        value={filters.moadianStatus}
                                                        onChange={(e) => setFilters({ ...filters, moadianStatus: e.target.value })}
                                                    >
                                                        <option value="">همه</option>
                                                        {moadianStatuses.map(s => (
                                                            <option key={s.value} value={s.value}>{s.label}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="col-md-3">
                                                    <label className="form-label">از تاریخ</label>
                                                    <JalaliDatePicker
                                                        value={filters.dateFrom}
                                                        onChange={(date) => setFilters({ ...filters, dateFrom: date })}
                                                        placeholder="انتخاب تاریخ"
                                                        showTime={true}
                                                    />
                                                </div>
                                                <div className="col-md-3">
                                                    <label className="form-label">تا تاریخ</label>
                                                    <JalaliDatePicker
                                                        value={filters.dateTo}
                                                        onChange={(date) => setFilters({ ...filters, dateTo: date })}
                                                        placeholder="انتخاب تاریخ"
                                                        showTime={true}
                                                    />
                                                </div>
                                                <div className="col-md-3">
                                                    <label className="form-label">از شماره صورتحساب</label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        placeholder="مثلا: 1001"
                                                        value={filters.invoiceNumFrom}
                                                        onChange={(e) => setFilters({ ...filters, invoiceNumFrom: e.target.value })}
                                                    />
                                                </div>
                                                <div className="col-md-3">
                                                    <label className="form-label">تا شماره صورتحساب</label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        placeholder="مثلا: 1050"
                                                        value={filters.invoiceNumTo}
                                                        onChange={(e) => setFilters({ ...filters, invoiceNumTo: e.target.value })}
                                                    />
                                                </div>
                                            </div>

                                            <div className="row mt-4">
                                                <div className="col-md-12 d-flex justify-content-end gap-2">
                                                    <button className="btn btn-outline-danger" onClick={clearFilters}>
                                                        <i className="feather-x me-1"></i>
                                                        حذف فیلترها
                                                    </button>
                                                    <button className="btn btn-primary" onClick={handleApplyFilters}>
                                                        <i className="feather-search me-1"></i>
                                                        جستجو
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden text-dark">در حال بارگذاری...</span>
                                </div>
                                <div className="mt-2 text-dark">در حال دریافت اطلاعات...</div>
                            </div>
                        ) : error ? (
                            <div className="text-center py-5 text-danger">
                                <i className="feather-alert-triangle display-4 mb-2"></i>
                                <p>{error}</p>
                                <button className="btn btn-sm btn-outline-secondary mt-2" onClick={() => fetchInvoices()}>
                                    تلاش مجدد
                                </button>
                            </div>
                        ) : (
                            <>
                            <div className="table-responsive">
                                <table className="table table-hover">
                                    <thead>
                                        <tr>
                                            <th>
                                                <input
                                                    type="checkbox"
                                                    onChange={handleSelectAll}
                                                    checked={invoices.data && invoices.data.length > 0 && selectedInvoices.length === invoices.data.length}
                                                />
                                            </th>
                                            <th>ردیف</th>
                                            <th>شماره صورتحساب</th>
                                            <th>تاریخ و زمان صدور</th>
                                            <th>موضوع صورتحساب</th>
                                            <th>مبلغ کل</th>
                                            <th>مالیات ارزش افزوده</th>
                                            <th>شناسه یکتا</th>
                                            <th>وضعیت سامانه مودیان</th>
                                            {/* TEMP-HIDDEN: ستون سند حسابداری — restore when needed
                                            <th>سند حسابداری</th>
                                            */}
                                            <th>عملیات</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {invoices.data && invoices.data.length > 0 ? (
                                            invoices.data.map((invoice, index) => (
                                                <tr key={invoice.id}>
                                                    <td>
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedInvoices.includes(invoice.id)}
                                                            onChange={() => handleSelectInvoice(invoice.id)}
                                                        />
                                                    </td>
                                                    <td>{index + 1}</td>
                                                    <td>{invoice.invoiceNum || '-'}</td>
                                                    <td>{toShamsiDate(invoice.finalDate)}</td>
                                                    <td>{formatInvoiceSubject(invoice.invoiceSubject)}</td>
                                                    <td>{invoice.sumTotalPrice?.toLocaleString('fa') || '0'}</td>
                                                    <td>{invoice.sumTaxPrice?.toLocaleString('fa') || '0'}</td>
                                                    <td>{invoice.uniqueIdentifier || '-'}</td>
                                                    <td>
                                                        <span className={`badge ${getStatusBadgeClass(invoice.moadianStatus)}`}>
                                                            {getStatusLabel(invoice.moadianStatus)}
                                                        </span>
                                                    </td>
                                                    {/* TEMP-HIDDEN: ستون سند حسابداری — restore when needed
                                                    <td>
                                                        {invoice.sale_accounting_document_submitted ? (
                                                            <div className="d-flex align-items-center gap-2 justify-content-center">
                                                                <span className="badge bg-success">
                                                                    سند فروش صادر شده
                                                                </span>
                                                                <button
                                                                    className="btn btn-sm btn-outline-info border-0 p-1"
                                                                    onClick={() => handlePrintSaleDoc(invoice.sale_accounting_document_id)}
                                                                    title="چاپ سند حسابداری فروش"
                                                                >
                                                                    <i className="feather-printer fs-6" />
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <span className="text-muted small">صادر نشده</span>
                                                        )}
                                                    </td>
                                                    */}
                                                    <td>
                                                        <Dropdown>
                                                            <Dropdown.Toggle
                                                                variant="outline-secondary"
                                                                size="sm"
                                                                id={`dropdown-actions-${invoice.id}`}
                                                            >
                                                                عملیات
                                                                <i className="feather-chevron-down ms-1"></i>
                                                            </Dropdown.Toggle>
                                                            <Dropdown.Menu popperConfig={{ strategy: 'fixed' }} renderOnMount>
                                                                <Dropdown.Item onClick={() => handleViewInvoice(invoice.id)}>
                                                                    <i className="feather-eye me-2"></i>
                                                                    مشاهده صورتحساب
                                                                </Dropdown.Item>
                                                                <Dropdown.Item
                                                                    disabled={!((invoice.moadianStatus == null || invoice.moadianStatus == 0 || invoice.moadianStatus == 1) && invoice.invoiceSubject !== 'Third')}
                                                                    onClick={() => handleEditInvoice(invoice.id)}
                                                                >
                                                                    <i className="feather-edit me-2"></i>
                                                                    ویرایش صورتحساب
                                                                </Dropdown.Item>
                                                                <Dropdown.Divider />
                                                                <Dropdown.Item
                                                                    disabled={!(invoice.moadianStatus != null && invoice.moadianStatus == 2)}
                                                                    onClick={() => handleCorrectionInvoice(invoice)}
                                                                >
                                                                    <i className="feather-edit-2 me-2"></i>
                                                                    اصلاح صورتحساب
                                                                </Dropdown.Item>
                                                                <Dropdown.Item
                                                                    disabled={!(invoice.moadianStatus != null && invoice.moadianStatus == 2)}
                                                                    onClick={() => handleCancellationInvoice(invoice)}
                                                                >
                                                                    <i className="feather-x-circle me-2"></i>
                                                                    ابطال صورتحساب
                                                                </Dropdown.Item>
                                                                <Dropdown.Item
                                                                    disabled={!(invoice.moadianStatus != null && invoice.moadianStatus == 2)}
                                                                    onClick={() => handleReturnedInvoice(invoice)}
                                                                >
                                                                    <i className="feather-rotate-ccw me-2"></i>
                                                                    برگشت از فروش صورتحساب
                                                                </Dropdown.Item>
                                                                <Dropdown.Divider />
                                                                <Dropdown.Item
                                                                    disabled={!!(invoice.uniqueIdentifier || !invoice.invoiceNum)}
                                                                    onClick={() => handleAssignUniqueId(invoice)}
                                                                >
                                                                    <i className="feather-hash me-2"></i>
                                                                    اختصاص شناسه یکتا
                                                                </Dropdown.Item>
                                                                <Dropdown.Item
                                                                    onClick={() => handleSendSingleToMoadian(invoice)} className='text-wrap'
                                                                >
                                                                    <i className="feather-send me-2"></i>
                                                                    ارسال صورتحساب به سامانه مودیان
                                                                </Dropdown.Item>
                                                                {/* TEMP-HIDDEN: ارسال به شبکه آرسین + سند حسابداری — restore when needed
                                                                {invoice.invoiceNum && (
                                                                    <>
                                                                    <Dropdown.Item
                                                                        onClick={() => handleSendToArsin(invoice)}
                                                                    >
                                                                        <i className="feather-share-2 me-2"></i>
                                                                        ارسال صورتحساب به شبکه ارسین
                                                                    </Dropdown.Item>
                                                                    <Dropdown.Item
                                                                        onClick={() => navigate(`/sale/form/?invoice_id=${invoice.id}`)}
                                                                    >
                                                                        <i className="feather-file-text me-2"></i>
                                                                        سند حسابداری
                                                                    </Dropdown.Item>
                                                                    </>
                                                                )}
                                                                */}
                                                                <Dropdown.Item
                                                                    onClick={() => handlePrintInvoice(invoice.id)}
                                                                >
                                                                    <i className="feather-printer me-2"></i>
                                                                    چاپ صورتحساب
                                                                </Dropdown.Item>
                                                                {invoice.moadianStatus != null && invoice.moadianStatus == 1 && (
                                                                    <Dropdown.Item
                                                                        onClick={() => handleViewErrors(invoice)}
                                                                    >
                                                                        <i className="feather-alert-circle me-2"></i>
                                                                        مشاهده خطا
                                                                    </Dropdown.Item>
                                                                )}
                                                                <Dropdown.Divider />
                                                                <Dropdown.Item
                                                                    className="text-danger"
                                                                    disabled={!((invoice.moadianStatus == null || invoice.moadianStatus == 0 || invoice.moadianStatus == 1) && invoice.id === Math.max(...invoices.data.map(i => i.id)))}
                                                                    onClick={() => handleDeleteInvoice(invoice)}
                                                                >
                                                                    <i className="feather-trash-2 me-2"></i>
                                                                    حذف صورتحساب
                                                                </Dropdown.Item>
                                                            </Dropdown.Menu>
                                                        </Dropdown>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="10" className="text-center py-4">
                                                    <div className="d-flex flex-column align-items-center">
                                                        <i className="feather-inbox display-4 mb-2 text-muted"></i>
                                                        <span className="text-muted">هیچ صورتحسابی یافت نشد.</span>
                                                        {hasActiveFilters() && (
                                                            <button className="btn btn-sm btn-link mt-2" onClick={clearFilters}>
                                                                حذف فیلترها و نمایش همه
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            {renderPagination()}
                            </>
                        )}
                    </div>
                </div>
            </div>

            <Modal show={showMoadianModal} onHide={() => setShowMoadianModal(false)} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>ارسال به سامانه مودیان</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-4">
                        <h6 className="mb-3">صورتحساب‌های انتخاب شده:</h6>
                        <div className="table-responsive">
                            <table className="table table-sm table-bordered">
                                <thead>
                                    <tr>
                                        <th>شماره صورتحساب</th>
                                        <th>تاریخ صورتحساب</th>
                                        <th>مبلغ صورتحساب</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {selectedMoadianInvoices.map((inv, idx) => (
                                        <tr key={idx}>
                                            <td>{inv.invoiceNum || '-'}</td>
                                            <td>{inv.finalDate || '-'}</td>
                                            <td>{inv.sumTotalPrice?.toLocaleString('fa') || '0'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <div className="border rounded p-3 text-center" style={{ borderStyle: 'dashed' }}>
                                <label className="form-label d-block">فایل کلید خصوصی را در اینجا رها کنید</label>
                                <input
                                    ref={privateKeyFileInputRef}
                                    type="file"
                                    className="form-control mt-2 d-none"
                                    onChange={(e) => handleFileChange(e, 'privateKeyFile')}
                                    accept=".pem,.key,.txt"
                                />
                                {moadianFiles.privateKeyFile && (
                                    <div className="mt-2 text-success">
                                        <i className="feather-check-circle me-1"></i>
                                        {moadianFiles.privateKeyFile.name}
                                    </div>
                                )}
                                <button 
                                    type="button"
                                    className="btn btn-primary btn-sm mt-2"
                                    onClick={() => privateKeyFileInputRef.current?.click()}
                                >
                                    انتخاب فایل
                                </button>
                            </div>
                        </div>
                        <div className="col-md-6 mb-3">
                            <div className="border rounded p-3 text-center" style={{ borderStyle: 'dashed' }}>
                                <label className="form-label d-block">فایل گواهی را در اینجا رها کنید</label>
                                <input
                                    ref={certificateFileInputRef}
                                    type="file"
                                    className="form-control mt-2 d-none"
                                    onChange={(e) => handleFileChange(e, 'certificateFile')}
                                    accept=".pem,.crt,.cer,.txt"
                                />
                                {moadianFiles.certificateFile && (
                                    <div className="mt-2 text-success">
                                        <i className="feather-check-circle me-1"></i>
                                        {moadianFiles.certificateFile.name}
                                    </div>
                                )}
                                <button 
                                    type="button"
                                    className="btn btn-primary btn-sm mt-2"
                                    onClick={() => certificateFileInputRef.current?.click()}
                                >
                                    انتخاب فایل
                                </button>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-between">
                    <button className="btn btn-secondary" onClick={() => setShowMoadianModal(false)}>
                        لغو
                    </button>
                    <div className="d-flex gap-2">
                        <button className="btn btn-warning" onClick={() => submitToMoadian(true)}>
                            تست کلید خصوصی و فایل سرتیفیکیت
                        </button>
                        <button className="btn btn-success" onClick={() => submitToMoadian(false)}>
                            ارسال به سامانه مودیان
                        </button>
                    </div>
                </Modal.Footer>
            </Modal>

            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>حذف صورتحساب</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center">
                    <div className="mb-4">
                        <i className="feather-alert-triangle display-4 text-danger"></i>
                    </div>
                    <p className="fs-5 mb-3">
                        آیا از حذف صورتحساب با شماره <span className="text-danger fw-bold">{invoiceToDelete?.invoiceNum}</span> اطمینان دارید؟
                    </p>
                    <p className="text-muted">
                        عملیات حذف صورتحساب بدون برگشت خواهد بود و همه اطلاعات صورتحساب پاک خواهند شد!
                    </p>
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-center gap-3">
                    <button className="btn btn-secondary px-4" onClick={() => setShowDeleteModal(false)}>
                        لغو
                    </button>
                    <button className="btn btn-danger px-4" onClick={confirmDeleteInvoice}>
                        حذف صورتحساب
                    </button>
                </Modal.Footer>
            </Modal>

            <Modal show={errorModal.show} onHide={() => setErrorModal({ show: false, invoice: null, errors: [] })} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>وضعیت در سامانه مودیان - صورتحساب شماره {errorModal.invoice?.invoiceNum}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className='d-flex justify-content-between mb-4'>
                        <div className='d-flex'>
                            <p className='mb-0'>شماره صورتحساب:</p>
                            <p className='me-2 mb-0'>{errorModal.invoice?.invoiceNum}</p>
                        </div>
                        <div className='d-flex'>
                            <p className='mb-0'>تاریخ صورتحساب:</p>
                            <p className='me-2 mb-0'>{errorModal.invoice?.finalDate || '-'}</p>
                        </div>
                        <div className='d-flex'>
                            <p className='mb-0'>مبلغ صورتحساب:</p>
                            <p className='me-2 mb-0'>{errorModal.invoice?.sumTotalPrice?.toLocaleString('fa') || '0'}</p>
                        </div>
                    </div>
                    {errorModal.errors.length > 0 ? (
                        <div className='d-flex flex-column'>
                            <p className='fw-bold mb-3'>خطاها:</p>
                            <div>
                                {errorModal.errors.map((error, index) => (
                                    <p key={index} style={{marginBottom: '8px'}}>
                                        - {typeof error === 'string' ? error.trim() : (error.message || String(error))}
                                    </p>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="alert alert-info">خطایی یافت نشد</div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-danger" onClick={() => setErrorModal({ show: false, invoice: null, errors: [] })}>
                        بستن
                    </button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default Invoices;
